import { useCallback, useState } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import useRest from '@/helper/hooks/useRest';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import routes from '@/pages/corporate/routes';

import { useP_SwitchWorkspaceMutation } from '../graphql/mutations/generated/p_SwitchWorkspace.productivity';
import type { SwitcherWorkspace } from '../utils';

export const SWITCH_WORKSPACE_URL = '/api/auth/switch-workspace';

/**
 * Moves both backends to the chosen workspace: Workeffy records it as the person's current one,
 * Lyearn re-issues the auth cookies for it, then the page reloads so every client cache is rebuilt
 * under the new scope.
 */
const useSwitchWorkspace = (personId?: string) => {
  const [switching, setSwitching] = useState(false);
  const { setLoginAndRefetch } = useUserContext();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const [, executeWorkeffySwitch] = useP_SwitchWorkspaceMutation();
  const [, , , executeMintToken] = useRest(SWITCH_WORKSPACE_URL);

  const switchWorkspace = useCallback(
    async (workspace: SwitcherWorkspace) => {
      if (!personId || switching) return;
      setSwitching(true);
      try {
        // Workeffy first: it checks membership and is safe to repeat, while Lyearn revokes the old token.
        const { error } = await executeWorkeffySwitch({
          personId,
          workspaceId: workspace.workeffyId,
        });
        if (error) throw error;

        // Lyearn reads the sub-workspace from the token: mint one for the target (sets cookies too).
        const response = await executeMintToken({
          body: JSON.stringify({ subWorkspaceId: workspace.id }),
        });
        if (!response?.success || !response.loginToken) {
          throw new Error(response?.errorCode ?? 'SWITCH_WORKSPACE_FAILED');
        }
        await setLoginAndRefetch(response.loginToken);

        // Full reload drops urql caches scoped to the old workspace.
        window.location.href = routes.Home();
      } catch (e) {
        ErrorReporter.error(e);
        showDefaultErrorSnackbar();
        setSwitching(false);
      }
    },
    [
      executeMintToken,
      executeWorkeffySwitch,
      personId,
      setLoginAndRefetch,
      showDefaultErrorSnackbar,
      switching,
    ],
  );

  return { switchWorkspace, switching };
};

export default useSwitchWorkspace;
